import React, { useState } from 'react';
import { TextField, Button, Typography, Grid, Container, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import axios from 'axios';

function App() {
  const [apiKey, setApiKey] = useState('');
  const [loadingApi, setLoadingApi] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [uid1, setUid1] = useState('');
  const [uid2, setUid2] = useState('');
  const [data1, setData1] = useState(null);
  const [data2, setData2] = useState(null);
  const [name1, setName1] = useState('');
  const [name2, setName2] = useState('');
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [error, setError] = useState('');

  const fetchUserDetails = async () => {
    setLoadingApi(true);
    setError('');
    try {
      const response = await axios.get(`https://api.torn.com/v2/user?key=${apiKey}`);
      
      if (!response.data.name) {
        setError('User name not found in response. Please check the API key.');
        return;
      }
  
      const uid = response.data.player_id;
      setUserDetails({
        uid: uid,
        name: response.data.name,
      });
      setUid1(uid); 
      setName1(response.data.name); 
    } catch (err) {
      console.error(err);
      if (err.response) {
        setError('Failed to fetch user details. Please check the API key.');
      } else if (err.request) {
        setError('Server is down or unreachable. Please try again later.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoadingApi(false);
    }
  };  

  const fetchData = async (uid, apiKey) => {
    try {
      const response = await axios.get(`https://api.torn.com/v2/user/${uid}/personalstats?key=${apiKey}&cat=crimes`);
      
      const crimeData = response.data.personalstats.crimes;
      const version = crimeData.version; 
  
      return { data: crimeData.offenses, version };
    } catch (err) {
      console.error(err);
      throw new Error('Failed to fetch data');
    }
  };

  const fetchUserName = async (uid, apiKey) => {
    try {
      const response = await axios.get(`https://api.torn.com/v2/user/${uid}/?key=${apiKey}`);
      return response.data.name;
    } catch (err) {
      console.error(err);
      throw new Error('Failed to fetch user name');
    }
  };

  const handleCompare = async () => {
    setLoadingComparison(true);
    setError('');
    try {
      const [dataForUid1, dataForUid2, fetchedName1, fetchedName2] = await Promise.all([
        fetchData(uid1, apiKey),
        fetchData(uid2, apiKey),
        fetchUserName(uid1, apiKey),
        fetchUserName(uid2, apiKey),
      ]);
  
      if (dataForUid1.version !== dataForUid2.version) {
        setError("Can't compare due to differences in Crime version.");
        setData1(null);  
        setData2(null);  
        setLoadingComparison(false);
        return;
      }
  
      setData1(dataForUid1.data);
      setData2(dataForUid2.data);
      setName1(fetchedName1);
      setName2(fetchedName2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingComparison(false);
    }
  };

  const renderComparisonTable = () => {
    if (!data1 || !data2) return null;
    
    const keys = Object.keys(data1);
    return (
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Crime Type</strong></TableCell>
              <TableCell align="center"><strong>{name1}</strong></TableCell>
              <TableCell align="center"><strong>{name2}</strong></TableCell>
              <TableCell align="center"><strong>Difference</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((key) => {
              const value1 = data1[key];
              const value2 = data2[key];
              const difference = value1 - value2;
  
              let rowStyle = {};
              if (value1 < value2) {
                rowStyle = { 
                  backgroundColor: '#cce5ff', 
                };
              } else if (value1 > value2) {
                rowStyle = { 
                  backgroundColor: '#fff3cd', 
                };
              }
  
              return (
                <TableRow key={key} style={rowStyle}>
                  <TableCell>{key}</TableCell>
                  <TableCell align="center">{value1}</TableCell>
                  <TableCell align="center">{value2}</TableCell>
                  <TableCell align="center">{difference}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom>
        Crime Comparison Tool
      </Typography>
      {!userDetails ? (
        <>
          <TextField
            fullWidth
            label="API Key"
            variant="outlined"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={fetchUserDetails}
            disabled={loadingApi}
          >
            Fetch Details
          </Button>
          {loadingApi && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
          {error && <Typography color="error" align="center">{error}</Typography>}
        </>
      ) : (
        <>
          <Typography variant="h6" align="center" gutterBottom>
            Welcome, {userDetails.name} (UID: {userDetails.uid})
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="UID 1"
                variant="outlined"
                value={uid1}
                onChange={(e) => setUid1(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="UID 2"
                variant="outlined"
                value={uid2}
                onChange={(e) => setUid2(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleCompare}
                disabled={loadingComparison}
              >
                Compare
              </Button>
            </Grid>
          </Grid>
          {loadingComparison && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
          {error && <Typography color="error" align="center">{error}</Typography>}
          {renderComparisonTable()}
        </>
      )}
    </Container>
  );
}

export default App;
